'use client'

import { useState, useEffect, useRef } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { cva, type VariantProps } from 'class-variance-authority'
import confetti from 'canvas-confetti'
import Pill from '@/components/ui/Pill'
import { Icon } from '@/icons/Icon'
import { cn, validateEmail } from '@/lib/utils'
import { submitForm } from '@/lib/actions/submit-form'
import { trackFormSubscribe } from '@/lib/analytics'
import { createLogger } from '@/lib/logger'

const logger = createLogger('EmailSignUp')

const container = cva(
  'flex h-fit items-center transition-colors gap-3 border rounded-xl py-3 pl-4 pr-3',
  {
    variants: {
      theme: {
        dark: 'border-white/15 bg-white/5',
        light: 'border-gray-200 bg-gray-50 dark:border-white/15 dark:bg-white/5',
      },
      response: {
        success: 'border-emerald-500',
        error: 'bg-red-500/10 border-red-500',
      },
    },
  },
)

type EmailSignUpProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof container> & {
    icon?: React.ReactNode
    focus?: string
    success?: string
    error?: string
    theme?: 'dark' | 'light'
    confettiX?: number
    maxWidth?: number
    hideIcon?: boolean
    button?: string | null
  }

export default function EmailSignUp({
  icon,
  focus,
  success,
  error,
  theme = 'light',
  confettiX = 0.4,
  maxWidth,
  button,
  hideIcon = false,
  className,
}: EmailSignUpProps) {
  const [response, setResponse] = useState<'success' | 'error' | null>(null)
  // Server-provided error (e.g. the blocked-domain work-email prompt, EA-820).
  // Takes precedence over the static `error` prop and the default message.
  const [serverError, setServerError] = useState<string | null>(null)
  const [email, setEmail] = useState<string>('')
  const [submitting, setSubmitting] = useState(false)
  const [buttonWidth, setButtonWidth] = useState<number>(0)
  const isSubmittingRef = useRef(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const buttonRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (response === 'success') {
      const confettiTimer = setTimeout(() => {
        confetti({
          particleCount: 60,
          startVelocity: 30,
          spread: 60,
          origin: {
            x: confettiX,
            y: 0.6,
          },
          colors: ['10B981'],
        })
      }, 300)
      return () => clearTimeout(confettiTimer)
    }
  }, [response, confettiX])

  useEffect(() => {
    const updateButtonWidth = () => {
      if (buttonRef.current) {
        setButtonWidth(buttonRef.current.offsetWidth)
      }
    }

    updateButtonWidth()

    window.addEventListener('resize', updateButtonWidth)

    return () => {
      window.removeEventListener('resize', updateButtonWidth)
    }
  }, [button, focus])

  const handleSubmit = async () => {
    if (isSubmittingRef.current) return
    if (!validateEmail(email)) {
      setServerError(null)
      setResponse('error')
      inputRef.current?.focus()
      return
    }

    isSubmittingRef.current = true
    setSubmitting(true)
    setServerError(null)

    try {
      await submitForm({
        type: 'email',
        email,
        pageUrl: window.location.href,
      })
      setResponse('success')
      trackFormSubscribe()
    } catch (error) {
      setServerError(error instanceof Error ? error.message : null)
      // Surfacing this so the bug Andra reported (people unable to opt in)
      // produces a queryable signal in Better Stack instead of silently
      // failing. No PII (full email) or sensitive URL params (query/hash).
      let safePageUrl: string | null = null
      if (typeof window !== 'undefined') {
        try {
          const url = new URL(window.location.href)
          safePageUrl = url.origin + url.pathname
        } catch {
          safePageUrl = null
        }
      }
      logger.error('Email signup submission failed', error, {
        emailDomain: email.split('@')[1] ?? null,
        emailLength: email.length,
        pageUrl: safePageUrl,
      })
      setResponse('error')
    } finally {
      isSubmittingRef.current = false
      setSubmitting(false)
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      handleSubmit()
    }
  }

  return (
    <div
      className="relative w-full shrink-0"
      style={{ maxWidth: maxWidth ? `${maxWidth}px` : undefined }}
    >
      <div className={cn(container({ response, theme, className }))} suppressHydrationWarning>
        {hideIcon
          ? null
          : icon ?? (
              <Icon
                name="at-sign"
                className={cn(theme === 'dark' ? 'text-white' : 'text-gray-950 dark:text-white')}
              />
            )}
        <input
          ref={inputRef}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter email"
          aria-label="Email address"
          disabled={response === 'success'}
          autoComplete="email"
          // Password managers / AV extensions inject attributes onto this input after SSR.
          // suppressHydrationWarning on the wrapper doesn't cover the input (it doesn't
          // cascade to children), so it must live on the input itself.
          suppressHydrationWarning
          data-dashlane-ignore="true"
          data-1p-ignore="true"
          data-lpignore="true"
          className={cn(
            'h-full w-full bg-transparent pb-0.5 text-16 outline-none transition-colors md:text-15',
            theme === 'dark'
              ? 'text-white placeholder-white/25'
              : 'text-gray-950 placeholder-gray-400 disabled:text-gray-400 dark:text-white dark:placeholder-white/25 dark:disabled:text-white/25',
          )}
          maxLength={100}
        />
        <Pill
          style="primary"
          onClick={handleSubmit}
          className={cn(
            'relative w-fit origin-right overflow-hidden transition-all duration-300 ease-in-out',
            response === 'success' && 'pointer-events-none bg-emerald-500',
            buttonWidth === 0 ? 'opacity-0' : 'opacity-100',
          )}
          customStyles={{
            width:
              buttonWidth > 0
                ? response === 'success'
                  ? '106px'
                  : `${buttonWidth + 32}px`
                : 'auto',
          }}
        >
          <div
            className={cn(
              'relative flex h-16 w-full flex-col text-right transition-transform duration-300 ease-in-out',
              response === 'success' ? '-translate-y-1/4' : 'translate-y-1/4',
            )}
          >
            <span
              ref={buttonRef}
              className={cn(
                'flex h-8 w-fit items-center justify-center whitespace-nowrap',
                focus ? 'sm:justify-end' : '',
              )}
            >
              {button || (
                <>
                  Subscribe
                  {focus && <span className="ml-1 hidden sm:flex">for {focus}</span>}
                </>
              )}
            </span>
            <span className="flex h-8 items-center justify-end whitespace-nowrap">Subscribed!</span>
          </div>
        </Pill>
      </div>

      <AnimatePresence>
        {response && (
          <motion.div
            key={response}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="mt-0.5 flex pt-2.5"
          >
            {response === 'success' ? (
              <div className="flex w-full items-center gap-2.5 text-14 text-emerald-500">
                <Icon name="check" />
                <span className="leading-tight">
                  {success ??
                    `${email} subscribed! Check your inbox to confirm. Check spam if needed`}
                </span>
              </div>
            ) : response === 'error' ? (
              <div className="flex items-center gap-2.5 text-14 text-red-500">
                <Icon name="triangle-warning" />
                {serverError || error || 'Please enter a valid email address'}
              </div>
            ) : null}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
